import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export const LEGACY_AUDIO_SPEED_OPTIONS = [0.75, 1] as const
export const LEGACY_AUDIO_LOOP_OPTIONS = [1, 3, 5, 9] as const
export const LEGACY_AUDIO_LOOP_CIRCUMFERENCE = 314.159

export type LegacyAudioControllerVariant =
  | 'audioBook'
  | 'flashcard'
  | 'listeningQuiz'
  | 'selfStudyBoxAudio'

export type LegacyAudioSettingsSection = 'loop' | 'menu' | 'speed'

interface UseLegacyAudioControllerOptions {
  readonly autoPlayKey?: string
  readonly autoPlayOnLoad?: boolean
  readonly defaultLoopLevel?: number
  readonly defaultPlaybackRate?: number
  readonly source?: string
  readonly variant: LegacyAudioControllerVariant
}

export function useLegacyAudioController({
  autoPlayKey,
  autoPlayOnLoad = false,
  defaultLoopLevel = 3,
  defaultPlaybackRate = 1,
  source,
  variant,
}: UseLegacyAudioControllerOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const autoStartedKeyRef = useRef<string | undefined>(undefined)
  const loadedSourceRef = useRef<string | undefined>(undefined)
  const loopRestartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loopActiveRef = useRef(false)
  const loopRemainingRef = useRef(defaultLoopLevel)

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoopActive, setIsLoopActive] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [loopLevel, setLoopLevel] = useState(defaultLoopLevel)
  const [loopRemaining, setLoopRemaining] = useState(defaultLoopLevel)
  const [playbackRate, setPlaybackRate] = useState(defaultPlaybackRate)
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLButtonElement | null>(null)
  const [settingsSection, setSettingsSection] =
    useState<LegacyAudioSettingsSection>('menu')

  const clearLoopRestartTimer = useCallback(() => {
    if (loopRestartTimerRef.current !== null) {
      clearTimeout(loopRestartTimerRef.current)
      loopRestartTimerRef.current = null
    }
  }, [])

  useEffect(() => clearLoopRestartTimer, [clearLoopRestartTimer])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  const handleAutoPlay = useCallback(() => {
    if (
      !autoPlayOnLoad ||
      !audioRef.current ||
      !audioRef.current.getAttribute('src') ||
      audioRef.current.dataset.audioPreload === 'loading' ||
      audioRef.current.dataset.audioPreload === 'idle' ||
      autoStartedKeyRef.current === autoPlayKey
    ) {
      return
    }

    autoStartedKeyRef.current = autoPlayKey
    audioRef.current.playbackRate = playbackRate
    void audioRef.current.play().catch(() => {
      setIsPlaying(false)
    })
  }, [autoPlayKey, autoPlayOnLoad, playbackRate])

  useEffect(() => {
    handleAutoPlay()
  }, [handleAutoPlay, source])

  const handleTogglePlayback = useCallback(() => {
    if (!audioRef.current) {
      return
    }

    audioRef.current.playbackRate = playbackRate
    if (audioRef.current.paused) {
      void audioRef.current.play().catch(() => undefined)
      return
    }

    audioRef.current.pause()
  }, [playbackRate])

  const handleToggleLoop = useCallback(() => {
    if (!audioRef.current) {
      return
    }

    const nextLoopActive = !loopActiveRef.current
    loopActiveRef.current = nextLoopActive
    setIsLoopActive(nextLoopActive)
    clearLoopRestartTimer()

    if (!nextLoopActive) {
      return
    }

    loopRemainingRef.current = loopLevel
    setLoopRemaining(loopLevel)
    audioRef.current.playbackRate = playbackRate
    void audioRef.current.play().catch(() => {
      setIsPlaying(false)
    })
  }, [clearLoopRestartTimer, loopLevel, playbackRate])

  const handleLoopLevelChange = useCallback((nextLoopLevel: number) => {
    setLoopLevel(nextLoopLevel)
    loopRemainingRef.current = nextLoopLevel
    setLoopRemaining(nextLoopLevel)
    setSettingsSection('menu')
  }, [])

  const handlePlaybackRateChange = useCallback((nextPlaybackRate: number) => {
    setPlaybackRate(nextPlaybackRate)
    if (audioRef.current) {
      audioRef.current.playbackRate = nextPlaybackRate
    }
    setSettingsSection('menu')
  }, [])

  const handleSeekPercent = useCallback((nextProgress: number) => {
    if (!audioRef.current || duration <= 0) {
      return
    }

    audioRef.current.currentTime = (nextProgress / 100) * duration
  }, [duration])

  const handleCloseSettings = useCallback(() => {
    setSettingsAnchor(null)
    setSettingsSection('menu')
  }, [])

  const handleLoadStart = useCallback(() => {
    if (loadedSourceRef.current !== source) {
      loadedSourceRef.current = source
      clearLoopRestartTimer()
      loopActiveRef.current = false
      setCurrentTime(0)
      setIsLoopActive(false)
      loopRemainingRef.current = loopLevel
      setLoopRemaining(loopLevel)
    }
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate
    }
    handleAutoPlay()
  }, [clearLoopRestartTimer, handleAutoPlay, loopLevel, playbackRate, source])

  const handleEnded = useCallback(() => {
    setCurrentTime(0)
    setIsPlaying(false)
    if (!loopActiveRef.current || !audioRef.current) {
      return
    }

    if (loopRemainingRef.current <= 0) {
      loopActiveRef.current = false
      setIsLoopActive(false)
      loopRemainingRef.current = loopLevel
      setLoopRemaining(loopLevel)
      return
    }

    const nextLoopRemaining = loopRemainingRef.current - 1
    loopRemainingRef.current = nextLoopRemaining
    setLoopRemaining(nextLoopRemaining)
    clearLoopRestartTimer()
    loopRestartTimerRef.current = setTimeout(() => {
      if (!loopActiveRef.current || !audioRef.current) {
        return
      }

      audioRef.current.currentTime = 0
      audioRef.current.playbackRate = playbackRate
      void audioRef.current.play().catch(() => {
        setIsPlaying(false)
      })
    }, 500)
  }, [clearLoopRestartTimer, loopLevel, playbackRate])

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const loopProgress = loopLevel > 0 ? (loopRemaining / loopLevel) * 100 : 0
  const loopDashOffset =
    LEGACY_AUDIO_LOOP_CIRCUMFERENCE -
    (LEGACY_AUDIO_LOOP_CIRCUMFERENCE * loopProgress) / 100

  const audioElementProps = useMemo(
    () => ({
      onEnded: handleEnded,
      onLoadedMetadata: (event: SyntheticEvent<HTMLAudioElement>) => {
        setDuration(event.currentTarget.duration)
      },
      onLoadStart: handleLoadStart,
      onPause: () => {
        setIsPlaying(false)
      },
      onPlay: () => {
        setIsPlaying(true)
      },
      onTimeUpdate: (event: SyntheticEvent<HTMLAudioElement>) => {
        setCurrentTime(event.currentTarget.currentTime)
      },
      ref: audioRef,
      src: source,
    }),
    [handleEnded, handleLoadStart, source],
  )

  return {
    audioElementProps,
    audioRef,
    currentTime,
    duration,
    handleCloseSettings,
    handleLoopLevelChange,
    handlePlaybackRateChange,
    handleSeekPercent,
    handleToggleLoop,
    handleTogglePlayback,
    isLoopActive,
    isPlaying,
    loopDashOffset,
    loopLevel,
    loopProgress,
    loopRemaining,
    playbackRate,
    progress,
    settingsAnchor,
    settingsSection,
    setSettingsAnchor,
    setSettingsSection,
    variant,
  }
}
